import { useMemo } from 'react';
import { useSelector } from 'react-redux';
import { selectCurrentUser } from 'features/auth/slice';

import useLocation from './useLocation';

const MONTHLY_PRICE = 12;
const YEARLY_PRICE = 96;
const TEAMS_PRICE = 150;

const applyDiscounts = (discounts, price) => {
  return discounts.reduce((memo, discount) => memo * (1 - discount), price);
};

const generatePlan = (currencyCode, interval, price, discounts) => {
  if (!currencyCode) return {};

  const discountPercentages = discounts.map((discount) => `${discount * 100}%`);

  return {
    id: `${currencyCode.toLowerCase()}-pro-${interval}`,
    interval,
    price,
    discountedPrice: applyDiscounts(discounts, price),
    discountString: discountPercentages.join(' + '),
  };
};

export default function usePlans() {
  const { discount: userDiscount } = useSelector(selectCurrentUser) || {};
  const { discount: PPPDiscount, location } = useLocation();
  const userDiscountIsValid = new Date(userDiscount?.expiresAt) > new Date();

  const monthly = useMemo(() => {
    const discounts = PPPDiscount ? [PPPDiscount] : [];

    return generatePlan(
      location?.currencyCode,
      'monthly',
      MONTHLY_PRICE * location?.exchangeRate,
      discounts
    );
  }, [location, PPPDiscount]);

  const yearly = useMemo(() => {
    const discounts = [];

    if (PPPDiscount) discounts.push(PPPDiscount);
    if (userDiscountIsValid) discounts.push(userDiscount.amount);

    return generatePlan(
      location?.currencyCode,
      'yearly',
      YEARLY_PRICE * location?.exchangeRate,
      discounts
    );
  }, [location, PPPDiscount, userDiscount]);

  const teams = useMemo(() => {
    const discounts = PPPDiscount ? [PPPDiscount] : [];

    return generatePlan(
      location?.currencyCode,
      'teams',
      TEAMS_PRICE * location?.exchangeRate,
      discounts
    );
  }, [location, PPPDiscount]);

  return { monthly, teams, yearly };
}
